import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppStateStatus } from '@fhs-legacy/frontend/src/components/ota-update-alert/use-app-state-status';
import { IOperatingHours, IRestaurantNode } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { useConfigValue } from '@fhs-legacy/frontend/src/hooks/configs/use-config-value';
import { useStoreOpenStatusGenerator } from '@fhs-legacy/frontend/src/hooks/use-service-mode-status';
import { ServiceMode } from '@fhs-legacy/frontend/src/state/service-mode';
import {
  checkIfDateIsWithinCloseTimeAndMinutes,
  getDateNowByTimezone,
  readableHoursIntervalToday,
  useIsMobileOrderingAvailable,
} from '@fhs-legacy/frontend/src/utils/restaurant';
import { TWELVE_HOUR_TIME_PARSE_FORMAT } from '@fhs-legacy/frontend/src/utils/restaurant/constants';

/** Minutes before close at which the "Closing Soon" badge appears. FHSG-825. */
const CLOSING_SOON_THRESHOLD_MINUTES = 30;

export interface StoreCardStatusResult {
  /** Amber pill above the store name. Only "Closing Soon" uses it. */
  badge?: string;
  /** Leading word of the status line, e.g. "Open" or "Closed". Rendered in bold. */
  statusText: string;
  /** Today's hours, rendered after the status text. Absent when the day has no hours. */
  hoursText?: string;
  isOpen: boolean;
  /** False when the store can't take an online order at all, which disables the CTA. */
  supportsMobileOrdering: boolean;
}

const hoursForServiceMode = (
  restaurant: IRestaurantNode,
  serviceMode?: ServiceMode
): IOperatingHours | null | undefined => {
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      return restaurant.driveThruHours;
    case ServiceMode.CURBSIDE:
      return restaurant.curbsideHours;
    case ServiceMode.PICKUP_WINDOW:
      return restaurant.pickupWindowHours;
    case ServiceMode.EAT_IN:
    case ServiceMode.TAKEOUT:
    default:
      return restaurant.diningRoomHours;
  }
};

/**
 * Resolves the badge and status line for a locator card. A store that can't take an order at
 * all is handled by the caller, which replaces this status line with the reason.
 */
export function useStoreCardStatus(restaurant: IRestaurantNode): StoreCardStatusResult {
  const { formatMessage } = useIntl();
  const timeFormatConfig = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  // Forces a re-render when the app is foregrounded so open/closed status stays truthful.
  const appStateStatus = useAppStateStatus();

  const { generateOpenStatusForStore } = useStoreOpenStatusGenerator();
  const { availablePickupServiceModes, isOpen } = useMemo(
    () => generateOpenStatusForStore(restaurant),
    // appStateStatus is here to re-judge open/closed when the app is foregrounded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generateOpenStatusForStore, restaurant, appStateStatus]
  );

  const isMobileOrderingAvailable = useIsMobileOrderingAvailable(restaurant);
  const supportsMobileOrdering = Boolean(restaurant.hasMobileOrdering) && isMobileOrderingAvailable;
  const operatingHours = hoursForServiceMode(restaurant, availablePickupServiceModes[0]);

  return useMemo(() => {
    const now = getDateNowByTimezone({ timeZone: restaurant.timezone || undefined });

    const hoursText = readableHoursIntervalToday(
      operatingHours,
      {
        open24HoursMessage: formatMessage({ id: 'open24Hours' }),
        timeFormat: timeFormatConfig,
      },
      now
    );

    const isClosingSoon =
      isOpen &&
      Boolean(operatingHours) &&
      checkIfDateIsWithinCloseTimeAndMinutes(
        operatingHours as IOperatingHours,
        now,
        CLOSING_SOON_THRESHOLD_MINUTES
      );

    return {
      badge: supportsMobileOrdering && isClosingSoon ? 'Closing Soon' : undefined,
      statusText: formatMessage({ id: isOpen ? 'open' : 'closed' }),
      hoursText,
      isOpen,
      supportsMobileOrdering,
    };
  }, [
    supportsMobileOrdering,
    isOpen,
    operatingHours,
    restaurant.timezone,
    timeFormatConfig,
    formatMessage,
  ]);
}
